import { CropOriginal as CropOriginalIcon } from '@mui/icons-material';
import LoopIcon from '@mui/icons-material/Loop';
import { Button, Divider, Grid, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import { Controller } from 'react-hook-form';
import { ResourceSelectorSidebar } from 'src/components/widget';
import { ImagePreview } from 'src/components/widget/resourceSelectorWidget/ResourceSelector.styled';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { ResourceSelectorWidget } from 'src/components/widget/resourceSelectorWidget/ResourceSelectorWidget';
import { lang } from 'src/utils';
import { getThumbnailSrc } from 'src/utils/gcf-helper';

import { StyledReplaceButton } from '../../../cod/headerSection/modules/CustomImageSelectionButton.styled';
import { PositionBox } from '../../PositionBox';
import { SizeBox } from '../../SizeBox';
import { useImageSectionController } from './hooks/ImageSectionController.hook';
import { ImageSectionControllerProps } from './ImageSectionController.interface';

export const ImageSectionController = ({
  control,
  customer,
  onChange: handleOnChange,
  errors,
  watch,
}: ImageSectionControllerProps) => {
  const {
    currentIndex,
    addNewImage,
    replaceImage,
    removeImage,
    openBackgroundDrawer,
    handleCloseBackgroundDrawer,
  } = useImageSectionController({
    customer,
    handleOnChange,
  });

  return (
    <Grid container padding={2} gap={1} direction="column">
      <Controller
        key={`${customer}.media`}
        name={`${customer}.media`}
        control={control}
        render={({ field }) => {
          const mediaArray = field.value ?? [];

          return (
            <>
              {errors?.media && (
                <Typography color="error" variant="caption">
                  {errors?.media.message}
                </Typography>
              )}

              {mediaArray.map((item, index) => (
                <Grid container direction="column" gap={1} key={index}>
                  <Typography>
                    {lang.image} {index + 1}
                  </Typography>
                  <Divider />
                  <ImagePreview>
                    <Grid item xs={12}>
                      <Controller
                        name={`${customer}.media.${index}.value`}
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                          <ResourceSelectorWidget
                            {...field}
                            ref={ref}
                            value={getThumbnailSrc(
                              watch(`${customer}.media.${index}.value`),
                            )}
                            disabled
                          />
                        )}
                      />
                    </Grid>
                  </ImagePreview>
                  {item.value && (
                    <>
                      <PositionBox
                        x={item.left || 0}
                        y={item.top || 0}
                        onChange={(left, top) => {
                          const media = cloneDeep(field.value ?? []);
                          media[index] = { ...item, left, top };
                          handleOnChange(
                            `${customer}.media`,
                            media,
                            field.onChange,
                          );
                        }}
                      />
                      <SizeBox
                        width={item.width}
                        height={item.height}
                        onChange={(width, height) => {
                          const media = cloneDeep(field.value ?? []);
                          media[index] = { ...item, width, height };
                          handleOnChange(
                            `${customer}.media`,
                            media,
                            field.onChange,
                          );
                        }}
                      />
                    </>
                  )}
                  <Grid container wrap="nowrap">
                    <Grid item xs={4}>
                      <StyledReplaceButton
                        component="label"
                        startIcon={<LoopIcon />}
                        variant="outlined"
                        onClick={() => replaceImage(index)}>
                        <Typography>{lang.replace}</Typography>
                      </StyledReplaceButton>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="text"
                        color="error"
                        component="label"
                        onClick={() => removeImage(index, field)}>
                        {lang.remove}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              ))}

              <Grid container direction="column" gap={1}>
                <Typography>
                  {lang.image} {mediaArray.length + 1}
                </Typography>
                <Divider />
                <Grid xs={6}>
                  <Button
                    variant="outlined"
                    component="label"
                    onClick={() => addNewImage(field)}
                    startIcon={<CropOriginalIcon />}>
                    {lang.uploadImage}
                  </Button>
                </Grid>
              </Grid>

              <ResourceSelectorSidebar
                label=""
                type={ResourceType.BACKGROUND}
                openResourceSelectorDrawer={openBackgroundDrawer}
                onToggleResourceSelectorDrawer={handleCloseBackgroundDrawer}
                onChange={(value: string) => {
                  const updatedMedia = cloneDeep(mediaArray);
                  if (
                    currentIndex !== null &&
                    currentIndex < updatedMedia.length
                  ) {
                    updatedMedia[currentIndex].value = value;
                  } else {
                    updatedMedia.push({ value, left: 0, top: 0 });
                  }
                  handleOnChange(
                    `${customer}.media`,
                    updatedMedia,
                    field.onChange,
                  );
                }}
                hasError={!!errors?.media}
                error={errors?.media?.message || false}
                defaultAssetName={mediaArray[0]?.value}
              />
            </>
          );
        }}
      />
    </Grid>
  );
};
